import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SUPABASE_REINIT_EVENT } from "@/lib/supabase";

export type ChannelStatus =
  | "SUBSCRIBED"
  | "CHANNEL_ERROR"
  | "TIMED_OUT"
  | "CLOSED"
  | "JOINING";

export type RealtimeStatus = "live" | "connecting" | "reconnecting" | "offline";

interface RealtimeConnectionContextValue {
  status: RealtimeStatus;
  setChannelStatus: (channelKey: string, status: ChannelStatus) => void;
  removeChannel: (channelKey: string) => void;
}

const noopValue: RealtimeConnectionContextValue = {
  status: "offline",
  setChannelStatus: () => {},
  removeChannel: () => {},
};

const RealtimeConnectionContext = createContext<RealtimeConnectionContextValue>(noopValue);

function deriveStatus(map: Map<string, ChannelStatus>): RealtimeStatus {
  if (map.size === 0) return "offline";
  let anySubscribed = false;
  let anyJoining = false;
  let allError = true;
  for (const s of map.values()) {
    if (s === "SUBSCRIBED") {
      anySubscribed = true;
      allError = false;
    } else if (s === "JOINING") {
      anyJoining = true;
      allError = false;
    }
  }
  if (anySubscribed) return "live";
  if (allError) return "reconnecting";
  if (anyJoining) return "connecting";
  return "reconnecting";
}

export function RealtimeConnectionProvider({ children }: { children: React.ReactNode }) {
  const mapRef = useRef<Map<string, ChannelStatus>>(new Map());
  const [status, setStatus] = useState<RealtimeStatus>("offline");

  const recompute = useCallback(() => {
    setStatus(deriveStatus(mapRef.current));
  }, []);

  const setChannelStatus = useCallback((channelKey: string, next: ChannelStatus) => {
    const prev = mapRef.current.get(channelKey);
    if (prev === next) return;
    mapRef.current.set(channelKey, next);
    recompute();
  }, [recompute]);

  const removeChannel = useCallback((channelKey: string) => {
    if (!mapRef.current.has(channelKey)) return;
    mapRef.current.delete(channelKey);
    recompute();
  }, [recompute]);

  // Reset on supabase reinit; channels will re-register themselves.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => {
      mapRef.current.clear();
      recompute();
    };
    window.addEventListener(SUPABASE_REINIT_EVENT, handler);
    return () => window.removeEventListener(SUPABASE_REINIT_EVENT, handler);
  }, [recompute]);

  const value = useMemo<RealtimeConnectionContextValue>(() => ({
    status,
    setChannelStatus,
    removeChannel,
  }), [status, setChannelStatus, removeChannel]);

  return (
    <RealtimeConnectionContext.Provider value={value}>
      {children}
    </RealtimeConnectionContext.Provider>
  );
}

export function useRealtimeStatus(): RealtimeStatus {
  return useContext(RealtimeConnectionContext).status;
}

export interface RealtimeConnectionControls {
  setChannelStatus: (channelKey: string, status: ChannelStatus) => void;
  removeChannel: (channelKey: string) => void;
}

export function useRealtimeConnectionControls(): RealtimeConnectionControls {
  const ctx = useContext(RealtimeConnectionContext);
  return useMemo(() => ({
    setChannelStatus: ctx.setChannelStatus,
    removeChannel: ctx.removeChannel,
  }), [ctx.setChannelStatus, ctx.removeChannel]);
}
